import { alpha } from '@material-ui/core';
import { Box, Typography } from '@mui/material';
import {
    APIProvider,
    AdvancedMarker,
    Map as GoogleMap,
} from '@vis.gl/react-google-maps';
import React, { useMemo } from 'react';
import { useGetIndividualAssetQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { theme } from '../../../../../shared/theme';
import { ItineraryItemType, TravelMode } from '../../../../enums/itinerary';
import {
    ItineraryItemDto,
    ItineraryItemLinkedBookingInfo,
    ItineraryMapItemDto,
} from '../../../../types/itinerary';
import { ITEM_TYPE_CONFIG } from './itineraryConstants';
import { getTitle } from './ItineraryUtils';
import Route from './Route';

type Props = {
    reservation: any;
    items: ItineraryItemDto[];
    selectedItem: ItineraryItemDto | null;
};

type MapMarkerGroupItem = {
    item: ItineraryMapItemDto;
    mapIndex: number;
};

type MapMarkerGroup = {
    key: string;
    position: { lat: number; lng: number };
    items: MapMarkerGroupItem[];
};

const getLocationKey = (lat: number, lng: number) =>
    `${lat.toFixed(6)},${lng.toFixed(6)}`;

const isSameLocation = (
    a: { lat: number; lng: number },
    b: { lat: number; lng: number },
) => getLocationKey(a.lat, a.lng) === getLocationKey(b.lat, b.lng);

const isItemSelected = (
    item: ItineraryItemDto,
    selectedItem: ItineraryItemDto,
) => {
    if (selectedItem.is7DayBooking) {
        return (
            item.is7DayBooking &&
            (item.itineraryItemInfo as ItineraryItemLinkedBookingInfo)
                .bookingId ===
                (
                    selectedItem.itineraryItemInfo as ItineraryItemLinkedBookingInfo
                ).bookingId
        );
    } else if (
        selectedItem.itineraryItemInfo.itemType ===
        ItineraryItemType.PartnerService
    ) {
        return (
            item.itineraryItemInfo.itemType ===
                ItineraryItemType.PartnerService &&
            item.itineraryItemInfo.id === selectedItem.itineraryItemInfo.id
        );
    } else {
        return item.id === selectedItem.id;
    }
};

const ItineraryMap = ({ reservation, items, selectedItem }: Props) => {
    const {
        data: module,
        isFetching,
        isError,
    } = useGetIndividualAssetQuery(`modules/${reservation.module.id}`, {
        skip: !reservation?.module.id,
    });
    const mainBookingLocation = {
        lat: module.lat,
        lng: module.lng,
    };

    const mainReservation: ItineraryMapItemDto = {
        startDate: reservation?.startDate,
        endDate: reservation?.endDate,
        startTime: reservation?.bookingInfo.checkinTime,
        endTime: reservation?.bookingInfo.checkoutTime,
        mainBookingId: reservation.id,
        is7DayBooking: true,
        autoGeneratedDescription: `Reservation of ${reservation.module.name}`,
        conflictingItineraryItems: [],
        attachments: [],
        address: {
            lat: module.lat,
            lng: module.lng,
            countryCode: module.countryCode,
            state: module.state,
            suburb: module.suburb,
            streetAddress: module.streetAddress,
            postcode: module.postcode,
        },
        isMainReservation: true,
        itineraryItemInfo: {
            itemType: reservation.module.type,
            name: module.name,
        },
    };

    const getMapItems = (): ItineraryMapItemDto[] => {
        const mapItems = items.flatMap((item) => {
            const itemType = item.itineraryItemInfo.itemType;
            if (itemType === ItineraryItemType.Flight) {
                const departureAirport =
                    item.itineraryItemInfo.departureAirport;
                const arrivalAirport = item.itineraryItemInfo.arrivalAirport;

                return departureAirport.lat &&
                    departureAirport.lng &&
                    arrivalAirport.lat &&
                    arrivalAirport.lng
                    ? [
                          {
                              ...item,
                              address: {
                                  lat: departureAirport.lat,
                                  lng: departureAirport.lng,
                                  countryCode: '',
                                  state: '',
                                  suburb: '',
                                  streetAddress: '',
                                  postcode: '',
                              },
                          },
                          {
                              ...item,
                              address: {
                                  lat: arrivalAirport.lat,
                                  lng: arrivalAirport.lng,
                                  countryCode: '',
                                  state: '',
                                  suburb: '',
                                  streetAddress: '',
                                  postcode: '',
                              },
                          },
                      ]
                    : [];
            } else if (itemType === ItineraryItemType.Transfer) {
                const pickup = item.itineraryItemInfo.pickupAddress?.lat
                    ? item.itineraryItemInfo.pickupAddress
                    : item.itineraryItemInfo.pickupAirport;
                const dropoff = item.itineraryItemInfo.dropoffAddress?.lat
                    ? item.itineraryItemInfo.dropoffAddress
                    : item.itineraryItemInfo.dropoffAirport;

                return pickup &&
                    dropoff &&
                    pickup.lat &&
                    pickup.lng &&
                    dropoff.lat &&
                    dropoff.lng
                    ? [
                          {
                              ...item,
                              address: {
                                  lat: pickup.lat,
                                  lng: pickup.lng,
                                  countryCode: '',
                                  state: '',
                                  suburb: '',
                                  streetAddress: '',
                                  postcode: '',
                              },
                          },
                          {
                              ...item,
                              address: {
                                  lat: dropoff.lat,
                                  lng: dropoff.lng,
                                  countryCode: '',
                                  state: '',
                                  suburb: '',
                                  streetAddress: '',
                                  postcode: '',
                              },
                          },
                      ]
                    : [];
            } else {
                return item?.address && item.address.lat && item.address.lng
                    ? [
                          {
                              ...item,
                              address: {
                                  lat: item.address.lat,
                                  lng: item.address.lng,
                                  countryCode: item.address.countryCode,
                                  state: item.address.state,
                                  suburb: item.address.suburb,
                                  streetAddress: item.address.streetAddress,
                                  postcode: item.address.postcode,
                              },
                          },
                      ]
                    : [];
            }
        });

        return [mainReservation, ...mapItems];
    };

    const mapItems = getMapItems();

    const selectedRoute = useMemo(() => {
        if (!selectedItem) return null;

        const addressPoints = mapItems.filter((item) =>
            selectedItem.is7DayBooking
                ? item.is7DayBooking &&
                  (item.itineraryItemInfo as ItineraryItemLinkedBookingInfo)
                      .bookingId ===
                      (
                          selectedItem.itineraryItemInfo as ItineraryItemLinkedBookingInfo
                      ).bookingId
                : item.id === selectedItem.id,
        );

        if (addressPoints.length === 0) return null;

        const route =
            addressPoints.length === 1
                ? {
                      origin: {
                          lat: mainReservation.address.lat,
                          lng: mainReservation.address.lng,
                      },
                      destination: {
                          lat: addressPoints[0].address.lat,
                          lng: addressPoints[0].address.lng,
                      },
                  }
                : {
                      origin: {
                          lat: addressPoints[0].address.lat,
                          lng: addressPoints[0].address.lng,
                      },
                      destination: {
                          lat: addressPoints[1].address.lat,
                          lng: addressPoints[1].address.lng,
                      },
                  };

        if (isSameLocation(route.origin, route.destination)) return null;

        return route;
    }, [selectedItem, mapItems, mainReservation]);

    const travelMode =
        selectedItem?.itineraryItemInfo.itemType === ItineraryItemType.Flight
            ? TravelMode.FLIGHT
            : TravelMode.DRIVING;

    const filteredMapItems = mapItems.filter(
        (item) =>
            item.itineraryItemInfo.itemType !== ItineraryItemType.Transfer,
    );

    const groupsMap = new Map<string, MapMarkerGroup>();

    filteredMapItems.forEach((item, mapIndex) => {
        const key = getLocationKey(item.address.lat, item.address.lng);
        const entry: MapMarkerGroupItem = { item, mapIndex };
        const existing = groupsMap.get(key);

        if (existing) {
            existing.items.push(entry);
            return;
        }

        groupsMap.set(key, {
            key,
            position: {
                lat: item.address.lat,
                lng: item.address.lng,
            },
            items: [entry],
        });
    });

    const markerGroups = Array.from(groupsMap.values()).map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => {
            if (a.item.isMainReservation) return -1;
            if (b.item.isMainReservation) return 1;
            return a.mapIndex - b.mapIndex;
        }),
    }));

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <Box
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    width: '100%',
                    height: 360,
                }}
            >
                <GoogleMap
                    defaultZoom={12}
                    defaultCenter={mainBookingLocation}
                    mapId='9064210e52cc2181f0dc0a4e'
                    style={{ height: '100%', width: '100%' }}
                    disableDefaultUI
                    fullscreenControl
                    gestureHandling='greedy'
                >
                    {markerGroups.map((group) => {
                        const pinItem =
                            group.items.find(
                                ({ item }) => item.isMainReservation,
                            )?.item ?? group.items[0].item;
                        const pinConfig =
                            ITEM_TYPE_CONFIG[
                                pinItem.itineraryItemInfo.itemType
                            ];
                        const PinIcon = pinConfig.icon;
                        const isGroup = group.items.length > 1;
                        const hasMainReservation = group.items.some(
                            ({ item }) => item.isMainReservation,
                        );
                        const showIndexes = !(
                            hasMainReservation && group.items.length === 1
                        );
                        const mapIndexes = group.items
                            .filter(({ item }) => !item.isMainReservation)
                            .map(({ mapIndex }) => mapIndex);

                        return (
                            <AdvancedMarker
                                key={group.key}
                                position={group.position}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        transition: 'transform 0.2s',
                                    }}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor:
                                                    pinConfig.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: `0 1px 4px ${alpha(theme.palette.primary.main, 0.5)}`,
                                            }}
                                        >
                                            <PinIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette
                                                        .background.default,
                                                }}
                                            />
                                        </Box>
                                        {showIndexes && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -6,
                                                    right: -8,
                                                    minWidth: 18,
                                                    height: 18,
                                                    borderRadius: 9,
                                                    backgroundColor:
                                                        theme.palette.background
                                                            .default,
                                                    border: `1px solid ${pinConfig.color}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    px: 0.25,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        fontWeight: 900,
                                                        lineHeight: 1,
                                                        color: pinConfig.color,
                                                    }}
                                                >
                                                    {mapIndexes.join(', ')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            mt: 0.25,
                                            maxWidth: isGroup ? 120 : 80,
                                        }}
                                    >
                                        {group.items.map(
                                            ({ item, mapIndex }) => {
                                                const itemConfig =
                                                    ITEM_TYPE_CONFIG[
                                                        item.itineraryItemInfo
                                                            .itemType
                                                    ];
                                                const isSelected =
                                                    selectedItem &&
                                                    isItemSelected(
                                                        item,
                                                        selectedItem,
                                                    );

                                                return (
                                                    <Box
                                                        key={
                                                            item.id ??
                                                            `${group.key}-${mapIndex}`
                                                        }
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 0.25,
                                                            px: 0.25,
                                                            borderRadius: 0.5,
                                                            backgroundColor:
                                                                isSelected
                                                                    ? alpha(
                                                                          itemConfig.color,
                                                                          0.15,
                                                                      )
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10,
                                                                fontWeight:
                                                                    isSelected
                                                                        ? 900
                                                                        : 700,
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .primary,
                                                                whiteSpace:
                                                                    'nowrap',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                textAlign:
                                                                    'center',
                                                            }}
                                                        >
                                                            {`${item.isMainReservation ? '' : mapIndex + '.'} ${getTitle(item)}`}
                                                        </Typography>
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </Box>
                                </Box>
                            </AdvancedMarker>
                        );
                    })}

                    {selectedRoute && (
                        <Route
                            origin={selectedRoute.origin}
                            destination={selectedRoute.destination}
                            travelMode={travelMode}
                        />
                    )}
                </GoogleMap>
            </Box>
        </APIProvider>
    );
};

export default ItineraryMap;
